import { motion } from "framer-motion";
import {
    KanbanSquare,
    MessageCircle,
    FileText,
    Brain,
    Users2,
    Receipt,
    BarChart3,
    GitBranch,
    Workflow,
    Timer,
    UserCog,
    Wallet,
} from "lucide-react";
import { TiltCard } from "../effects/TiltCard";

const Card = ({ className = "", children, testId, tiltMax = 6 }) => (
    <TiltCard
        max={tiltMax}
        glare={false}
        className={`group relative bg-obsidian-200 hover:bg-obsidian-100 border border-white/[0.06] hover:border-white/20 rounded-2xl overflow-hidden transition-colors duration-300 hover:shadow-[0_30px_80px_-25px_rgba(59,130,246,0.5)] ${className}`}
    >
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="h-full w-full"
            data-testid={testId}
        >
            {children}
        </motion.div>
    </TiltCard>
);

const Tag = ({ icon: Icon, children }) => (
    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
        {Icon && <Icon className="w-3 h-3" />}
        <span>{children}</span>
    </div>
);

export const BentoFeatures = () => {
    return (
        <section
            id="modules"
            className="relative py-24 sm:py-32"
            data-testid="bento-features-section"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8">
                <div className="max-w-3xl mb-16">
                    <p className="text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">
                        14 MODULES · ZERO INTEGRATIONS
                    </p>
                    <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.08]">
                        Every tool your team needs.{" "}
                        <span className="text-zinc-500">Already inside.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-12 gap-4 sm:gap-5">
                    {/* Project Management */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-project-management"
                    >
                        <Tag icon={KanbanSquare}>
                            Projects · Tasks · Sprints
                        </Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Kanban, sprints & roadmaps in one place.
                        </h3>
                        <div className="mt-4 space-y-2">
                            {[
                                { name: "Sprint 21", w: "78%", c: "from-electric to-electric-deep" },
                                { name: "Q4 launch", w: "45%", c: "from-amber-glow to-amber-500" },
                                { name: "Onboarding", w: "92%", c: "from-emerald-500 to-emerald-700" },
                            ].map((b) => (
                                <div key={b.name}>
                                    <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
                                        <span>{b.name}</span>
                                        <span className="font-mono tabular-nums">{b.w}</span>
                                    </div>
                                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                                        <div
                                            className={`h-full bg-gradient-to-r ${b.c}`}
                                            style={{ width: b.w }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Team Chat */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-team-chat"
                    >
                        <Tag icon={MessageCircle}>Team Chat</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Channels, threads & video — context‑aware.
                        </h3>
                        <div className="mt-4 space-y-1.5">
                            {[
                                { name: "#sprint-21", color: "bg-emerald-500/80" },
                                { name: "#deals-q4", color: "bg-amber-glow" },
                                { name: "#design-crit", color: "bg-electric" },
                            ].map((c) => (
                                <div
                                    key={c.name}
                                    className="flex items-center gap-2 p-2 rounded-md bg-white/[0.03] border border-white/[0.05]"
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${c.color}`} />
                                    <span className="text-[11px] font-mono text-zinc-300 truncate">
                                        {c.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* CRM */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-crm"
                    >
                        <Tag icon={Users2}>CRM & Sales</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Deals you can close, not just track.
                        </h3>
                        <div className="mt-4 grid grid-cols-3 gap-1.5">
                            {[
                                { stage: "Lead", n: 142, c: "bg-zinc-700" },
                                { stage: "Qual", n: 67, c: "bg-electric" },
                                { stage: "Won", n: 23, c: "bg-emerald-500" },
                            ].map((s) => (
                                <div
                                    key={s.stage}
                                    className="rounded-md bg-white/[0.03] border border-white/[0.06] p-2"
                                >
                                    <div className={`w-1 h-4 ${s.c} rounded-full mb-1.5`} />
                                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">
                                        {s.stage}
                                    </div>
                                    <div className="font-outfit text-sm font-bold text-white tabular-nums">
                                        {s.n}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 text-[11px] text-zinc-500">
                            Pipeline{" "}
                            <span className="text-white font-semibold">$1.42M</span>
                        </div>
                    </Card>

                    {/* Notes & Docs */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-notes"
                    >
                        <Tag icon={FileText}>Notes & Docs</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Wiki‑grade documentation, alive with data.
                        </h3>
                        <div className="mt-4 space-y-1.5">
                            {[
                                "Quarterly OKRs",
                                "Engineering RFCs",
                                "Onboarding 101",
                                "Brand guidelines",
                            ].map((t) => (
                                <div
                                    key={t}
                                    className="flex items-center gap-2 text-xs text-zinc-400"
                                >
                                    <FileText className="w-3 h-3 text-zinc-600" />
                                    {t}
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Mind maps */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-mindmaps"
                    >
                        <Tag icon={Brain}>Mind Maps · Whiteboards</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Think in shapes. Ship in tasks.
                        </h3>
                        <div className="mt-4 relative h-24">
                            <span className="absolute left-1 top-3 w-12 h-12 rounded-lg border border-electric/40 bg-electric/10" />
                            <span className="absolute left-16 top-9 w-10 h-10 rounded-full border border-amber-glow/40 bg-amber-glow/10" />
                            <span className="absolute right-2 top-1 w-14 h-8 rounded-md border border-white/15 bg-white/5" />
                            <span className="absolute right-6 bottom-1 w-9 h-9 rotate-12 border border-white/15 bg-white/5" />
                        </div>
                    </Card>

                    {/* Invoicing */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-invoicing"
                    >
                        <Tag icon={Receipt}>Invoicing & Accounting</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Send. Track. Reconcile.
                        </h3>
                        <div className="mt-4 rounded-lg border border-white/10 p-3 bg-white/[0.03]">
                            <div className="flex items-center justify-between text-xs">
                                <span className="text-zinc-500">INV-00241</span>
                                <span className="text-emerald-400 font-semibold">
                                    Paid
                                </span>
                            </div>
                            <div className="font-outfit text-2xl font-bold text-white mt-1 tabular-nums">
                                $4,820.00
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-1">
                                Acme Corp · 2 days ago
                            </div>
                        </div>
                    </Card>

                    {/* Expenses */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-expenses"
                    >
                        <Tag icon={Wallet}>Expenses & Purchases</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Approvals that close in minutes.
                        </h3>
                        <div className="mt-4 flex items-end gap-1.5 h-16">
                            {[40, 60, 30, 70, 50, 85, 65].map((h, i) => (
                                <span
                                    key={i}
                                    className="flex-1 rounded-sm bg-gradient-to-t from-electric/30 to-electric"
                                    style={{ height: `${h}%` }}
                                />
                            ))}
                        </div>
                    </Card>

                    {/* Dashboards */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-dashboards"
                    >
                        <Tag icon={BarChart3}>Dashboards & Analytics</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Real‑time KPIs across every module.
                        </h3>
                        <div className="mt-4 space-y-2">
                            {[
                                { k: "MRR", v: "$182k", d: "+8.2%" },
                                { k: "Active users", v: "12,408", d: "+1.4%" },
                                { k: "NPS", v: "64", d: "+3 pts" },
                            ].map((m) => (
                                <div
                                    key={m.k}
                                    className="flex items-center justify-between rounded-md bg-white/[0.03] border border-white/[0.06] px-2.5 py-1.5"
                                >
                                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                                        {m.k}
                                    </span>
                                    <span className="flex items-baseline gap-1.5">
                                        <span className="font-outfit text-xs font-bold text-white tabular-nums">
                                            {m.v}
                                        </span>
                                        <span className="text-[10px] text-emerald-400">
                                            {m.d}
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Roadmaps & Gantt */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-roadmaps"
                    >
                        <Tag icon={GitBranch}>Roadmaps & Gantt</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Plan years. Adapt weekly.
                        </h3>
                        <div className="mt-4 space-y-2">
                            {[
                                { w: "70%", c: "from-electric to-electric-deep" },
                                { w: "45%", c: "from-amber-glow to-amber-500" },
                                { w: "85%", c: "from-emerald-500 to-emerald-700" },
                            ].map((b, i) => (
                                <div
                                    key={i}
                                    className="h-2 rounded-full bg-white/5 overflow-hidden"
                                >
                                    <div
                                        className={`h-full bg-gradient-to-r ${b.c}`}
                                        style={{ width: b.w }}
                                    />
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Forms & Workflow Automation */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-automation"
                    >
                        <Tag icon={Workflow}>Forms · Automations</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            If‑this‑then‑that, supercharged with AI.
                        </h3>
                        <div className="mt-4 flex items-center gap-1.5 text-[11px] text-zinc-400">
                            <span className="px-2 py-1 rounded bg-electric/15 text-electric border border-electric/20">
                                Form
                            </span>
                            <span className="text-zinc-600">→</span>
                            <span className="px-2 py-1 rounded bg-amber-glow/10 text-amber-glow border border-amber-glow/20">
                                AI
                            </span>
                            <span className="text-zinc-600">→</span>
                            <span className="px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                CRM
                            </span>
                        </div>
                    </Card>

                    {/* Timesheets */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-timesheets"
                    >
                        <Tag icon={Timer}>Timesheets · Time</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            Time, billable in one click.
                        </h3>
                        <div className="mt-4 flex items-center gap-3">
                            <div className="font-outfit text-3xl font-semibold text-white tabular-nums">
                                07:42
                            </div>
                            <span className="px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20">
                                tracking
                            </span>
                        </div>
                    </Card>

                    {/* HR */}
                    <Card
                        className="col-span-12 sm:col-span-6 lg:col-span-3 p-7"
                        testId="bento-hr"
                    >
                        <Tag icon={UserCog}>HR · Operations</Tag>
                        <h3 className="font-outfit text-lg font-semibold text-white mt-3">
                            People, performance & policies.
                        </h3>
                        <div className="mt-4 flex -space-x-2">
                            {["#3b82f6", "#fbbf24", "#22c55e", "#a78bfa", "#f43f5e"].map(
                                (c, i) => (
                                    <span
                                        key={i}
                                        className="w-8 h-8 rounded-full border-2 border-obsidian-200"
                                        style={{ background: c }}
                                    />
                                ),
                            )}
                            <span className="w-8 h-8 rounded-full border-2 border-obsidian-200 bg-white/5 flex items-center justify-center text-[10px] text-zinc-400">
                                +24
                            </span>
                        </div>
                    </Card>
                </div>
            </div>
        </section>
    );
};
